import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.2 } },
};

export default function MobileApp() {
  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">

      {/* HERO */}
      <motion.section
        className="relative h-screen flex items-center justify-center text-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=1600&q=80')",
        }}
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
      >
        <div className="absolute inset-0 bg-black/70"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <motion.h1
            className="text-5xl md:text-6xl font-extrabold mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Mobile App <span className="text-orange-500">Development</span>
          </motion.h1>

          <motion.p
            className="text-gray-300 text-lg max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            High-performance Android & iOS apps designed for scale, security,
            and user engagement.
          </motion.p>
        </div>
      </motion.section>

      {/* INTRO */}
      <motion.section
        className="py-24 max-w-6xl mx-auto px-6"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <h2 className="text-3xl font-bold text-orange-600 mb-6">
          Apps That Users Love
        </h2>
        <p className="text-lgtext-gray-300 leading-relaxed">
          We build intuitive, fast, and reliable mobile applications using
          modern frameworks and best practices. From idea to launch, we focus
          on performance, UX, and long-term maintainability.
        </p>
      </motion.section>

      {/* FEATURES */}
      <motion.section
        className="py-24 bg-gray-900"
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <motion.h2
            className="text-3xl font-bold text-center text-orange-600 mb-14"
            variants={fadeUp}
          >
            Core Features
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-10">
            <Feature
              title="Cross Platform"
              desc="Single codebase using React Native & Flutter."
            />
            <Feature
              title="High Performance"
              desc="Optimized for speed, smooth animations & stability."
            />
            <Feature
              title="Secure Architecture"
              desc="Data protection with enterprise-grade security."
            />
          </div>
        </div>
      </motion.section>

      {/* PROCESS */}
      <motion.section
        className="py-24 max-w-6xl mx-auto px-6"
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <motion.h2
          className="text-3xl font-bold text-orange-600 mb-14 text-center"
          variants={fadeUp}
        >
          App Development Process
        </motion.h2>

        <div className="grid md:grid-cols-4 gap-8 text-center">
          {[
            ["Ideation", "Requirement analysis & planning"],
            ["UI / UX", "Wireframes & interactive design"],
            ["Development", "Agile coding & integrations"],
            ["Launch & Support", "Testing, release & updates"],
          ].map(([title, desc], i) => (
            <motion.div
              key={i}
              className="p-8 bg-gray-800 rounded-3xl shadow"
              variants={fadeUp}
            >
              <h3 className="font-semibold text-lg mb-2">{title}</h3>
              <p className="text-sm text-gray-300">{desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* TECHNOLOGY */}
      <motion.section
        className="py-24 bg-gray-900"
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <motion.h2
            className="text-3xl font-bold text-orange-600 mb-12 text-center"
            variants={fadeUp}
          >
            Technologies We Use
          </motion.h2>

          <div className="grid md:grid-cols-5 gap-6 text-center">
            {["React Native", "Flutter", "Firebase", "Node.js", "REST APIs"].map(
              (tech, i) => (
                <motion.div
                  key={i}
                  className="p-6 rounded-2xl bg-gray-800 shadow font-medium"
                  variants={fadeUp}
                >
                  {tech}
                </motion.div>
              )
            )}
          </div>
        </div>
      </motion.section>

      {/* STATS */}
      <motion.section
        className="py-24 max-w-6xl mx-auto px-6"
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <div className="grid md:grid-cols-3 gap-10 text-center">
          <Stat value="100+" label="Apps Delivered" />
          <Stat value="4.8★" label="Average App Rating" />
          <Stat value="99.9%" label="Crash-Free Rate" />
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section
        className="py-28 text-center bg-gradient-to-r from-orange-700 to-orange-900 text-white"
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="text-4xl font-extrabold mb-6">
          Ready to Launch Your Mobile App?
        </h2>
        <p className="text-orange-100 mb-10">
          Let’s build a powerful app that users love.
        </p>
        <motion.a
          href="/contact"
          className="inline-block px-12 py-4 bg-black rounded-full text-orange-400 font-semibold"
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
        >
          Get Started
        </motion.a>
      </motion.section>
    </div>
  );
}

/* Components */

function Feature({ title, desc }) {
  return (
    <motion.div
      className="p-10 rounded-3xl bg-gray-800 shadow"
      variants={fadeUp}
      whileHover={{ y: -8 }}
    >
      <h3 className="text-xl font-semibold mb-3">{title}</h3>
      <p className="text-gray-300">{desc}</p>
    </motion.div>
  );
}

function Stat({ value, label }) {
  return (
    <motion.div
      className="p-10 rounded-3xl bg-gray-800 shadow"
      variants={fadeUp}
    >
      <h3 className="text-4xl font-extrabold text-orange-600 mb-2">{value}</h3>
      <p className="text-gray-300">{label}</p>
    </motion.div>
  );
}
